(ns leg.articles
  "The list: what there is to read, newest first.

  A row is a headline and, under it, which paper it came from and when it
  appeared. Nothing of the article itself: a line of its text used to be
  here, and it cost a third of the screen to say what the headline had
  already said in most rows and nothing at all in the rest - a feed's
  first sentence is as often the dateline, the byline or the headline over
  again as it is a summary. What that height buys instead is rows: a
  phone showed five articles and now shows nine, and a list long enough to
  be read down is worth more than a list that explains itself.

  A picture, when the feed came with one and the settings allow it
  (`:reader/images`): a square crop at the left of the row, in front of the
  headline. Behind a switch because it is a real choice and not a default
  somebody has to live with - pictures make a list to skim, headlines make
  a list to read.

  A headline is set in the display serif and stays bold whether it has been
  read or not - a newspaper does not set half its page in a lighter cut.
  What has been read is dimmer, and that is the only thing that marks it:
  the list is read from the top, and a page of badges is a page nobody
  reads."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [clojure.string :as str]
            [kc.core :as kc]
            [leg.mui :as mui :refer [sx]]
            [leg.nav :as nav]
            [leg.route :as route]
            [leg.scroll :as scroll]
            [leg.state :as state]
            [leg.theme :as theme]
            [leg.util :as u]))

(def-ui Thumb
  "The picture that came with an article, beside its headline: a square,
  cropped, the height of the tallest text a row usually has - two lines of
  headline and the kicker under them.

  Square and cropped because a list has to stay a list - a column of rows
  of different heights is read as a page of cards, and twenty cards is not
  twenty headlines. `alt=\"\"` because the thing it illustrates is written
  next to it: a screen reader announcing the publisher's file name before
  every headline would make the list worse, not more accessible.

  A dead address hides the whole element rather than leaving the browser's
  broken-image mark in the row. Feeds point at pictures that have been
  moved, and a column of grey icons is worse than no pictures at all."
  [src]
  (let [[broken? set-broken!] (kc/use-state false)]
    (when-not broken?
      ($ mui/Box
         {:component "img"
          :src src
          :alt ""
          :loading "lazy"
          :decoding "async"
          :referrerPolicy "no-referrer"
          :onError (fn [_] (set-broken! true))
          :sx (sx {:flex "none" :display "block"
                   :width "3.5rem" :height "3.5rem"
                   :objectFit "cover" :borderRadius "2px"
                   :backgroundColor "action.hover"
                   ;; Where `text-box-trim` is not understood, the headline
                   ;; still carries the empty strip over its capitals and
                   ;; the picture has to come down by hand. A quarter of a
                   ;; rem is what that strip measures in the display serif
                   ;; at this size - near enough in the grotesque, and the
                   ;; browsers this is for are a shrinking set.
                   "@supports not (text-box-trim: trim-start)"
                   {:marginTop ".25rem"}})}))))

(def ^:private press-ms
  "How long a finger has to stay on a row before it means something other
  than opening it. Long enough that a tap never reaches it, short enough
  that nobody lets go wondering whether it worked."
  450)

(def ^:private press-slack
  "How far that finger may travel in the meantime and still be a press, in
  pixels. A list is scrolled with the same finger in the same place, so
  anything that moves is a scroll and not a hold."
  10)

(defonce ^:private !press
  ;; The press being waited on: where the finger came down, the timer that
  ;; is counting, and whether the wait is over. One atom for the whole
  ;; list, because there is one finger - the same arrangement as the drag
  ;; in `leg.vt`, and for the same reason.
  ;;
  ;; `:fired?` outlives the touch by exactly one click. A long press is
  ;; still a tap as far as the browser is concerned: the finger comes up
  ;; and a click follows, and that click would open the article the menu
  ;; was just asked about.
  (atom nil))

(defn- stop-press!
  "Nothing more will come of this touch. `:fired?` is left where it is on
  purpose - the click it has to swallow has not happened yet."
  []
  (when-let [t (:timer @!press)] (js/clearTimeout t))
  (swap! !press dissoc :timer :x :y))

(def ^:private menu-grace
  "How long the menu ignores a press outside itself, in milliseconds. The
  finger that asked for it is still down: it comes up over the menu's own
  backdrop, the browser turns that into a click, and without this the menu
  would be gone in the frame it appeared in."
  400)

(def-ui Row
  "One article, as a button.

  Pressed, it opens. Held - or right-clicked, which is the same question
  asked with a mouse - it offers what can be done to the list at this line
  instead (`on-menu`, drawn by `ArticlesPane`). Held and not swiped,
  because a row lives in a column that scrolls under the same finger."
  [article selected? feed-title image? on-open on-menu]
  (let [{:article/keys [id title published first-seen read image]} article
        unread? (not read)]
    ($ mui/ListItemButton
       {:selected (boolean selected?)
        ;; What the keyboard walks along (see leg.keys). A row is a real
        ;; button, so moving the focus to it is the whole of it.
        :className "leser-row"
        ;; And what the remembered position is written down against
        ;; (`leg.scroll`): a list comes back to the row it was left on, so
        ;; the row has to be findable in the box by its own id.
        :data-row id
        :onClick (fn [_]
                   (if (:fired? @!press)
                     (swap! !press dissoc :fired?)
                     (on-open id)))
        :onContextMenu (fn [^js e]
                         (.preventDefault e)
                         (on-menu id (.-currentTarget e)))
        :onTouchStart (fn [^js e]
                        ;; The element and not the event: the timer fires
                        ;; long after the event has been handed back, and
                        ;; `currentTarget` is only set while it is being
                        ;; dispatched.
                        (let [el (.-currentTarget e)
                              t (aget (.-touches e) 0)]
                          (stop-press!)
                          (reset! !press
                                  {:x (.-clientX t)
                                   :y (.-clientY t)
                                   :timer (js/setTimeout
                                           (fn []
                                             (swap! !press assoc :timer nil :fired? true)
                                             (on-menu id el))
                                           press-ms)})))
        :onTouchMove (fn [^js e]
                       (let [{:keys [x y]} @!press
                             t (aget (.-touches e) 0)]
                         (when (and x t
                                    (> (+ (js/Math.abs (- (.-clientX t) x))
                                          (js/Math.abs (- (.-clientY t) y)))
                                       press-slack))
                           (stop-press!))))
        :onTouchEnd (fn [_] (stop-press!))
        :onTouchCancel (fn [_] (stop-press!))
        ;; The space around a row is what makes it one thing. Lines set a
        ;; hair apart inside a row that is a hair from the next row are
        ;; twenty lines in a column, not ten articles: what is between the
        ;; groups has to be plainly more than what is inside one, and the
        ;; whole of this row's spacing is that one ratio, measured in ink
        ;; and not in boxes (see the margin below). `.65rem` top and
        ;; bottom puts about twenty-one pixels of newsprint between the
        ;; kicker of one article and the first letter of the next, against
        ;; the ten inside - a hair over two to one.
        ;;
        ;; It was `.55rem` while the headline was trimmed at its baseline,
        ;; which made the distance inside the row six pixels and this one
        ;; three times it. The headline keeps its descenders now, so the
        ;; inside grew by four, and the outside had to grow with it or the
        ;; ratio would have been the one thing this spacing is.
        ;;
        ;; The gap to the picture is the row's own left padding again, so
        ;; the headline of a row with a picture and the headline of one
        ;; without are two steps of the same size from the edge.
        :sx (sx {:alignItems "flex-start" :gap ".75rem"
                 :paddingTop ".65rem" :paddingBottom ".65rem"
                 ;; A long press on text is the phone's own gesture -
                 ;; select the word, show the magnifier, offer to copy.
                 ;; Nobody selects a headline out of a list, and a row
                 ;; that answered a hold with a selection would never get
                 ;; as far as the menu below.
                 :userSelect "none"
                 :WebkitUserSelect "none"
                 :WebkitTouchCallout "none"})}
       ;; Left of the text, where a paper sets a cut: a column is read
       ;; down its left edge, and a picture on the right would have the eye
       ;; start again at every row that has one. A row without a picture -
       ;; which is most of them in most feeds - is laid out exactly as it
       ;; was before there were any.
       (when (and image? image) ($ Thumb {:src image}))
       ;; No gap on the column: what the eye measures is the distance
       ;; between the letters, so the one margin below is the distance
       ;; wanted less the leading the blocks already have - which is why
       ;; it is an odd little number and not a step of a scale.
       ($ :div {:style {:flex "1" :minWidth 0 :display "flex"
                        :flexDirection "column"}}
          ;; What has been read is dimmer, and only the headline is - the
          ;; row used to be, and two opacities multiply: 0.62 over the
          ;; kicker's 0.7 left the feed's name at 2.7:1 against newsprint,
          ;; which is not text any more. Dimmer here means the second ink,
          ;; which is a colour that was measured.
          ($ mui/Box {:component "span"
                      :sx (sx {:minWidth 0 :fontFamily theme/font-head
                               ;; The regular and not the bold. A column
                               ;; of headlines is already a column of
                               ;; headlines - the face, the size and the
                               ;; kicker underneath each say so - and
                               ;; forty of them set bold is a page with
                               ;; nothing quiet on it. Bold is kept for
                               ;; the one headline that is a front page
                               ;; (`Masthead`, `leg.page`) and for the
                               ;; article being read.
                               :fontWeight 400 :fontSize "1.05rem" :lineHeight 1.2
                               :letterSpacing "-.01em"
                               ;; The strip of air a line of type carries
                               ;; over its capitals is cut away, so the
                               ;; top of the headline is the top of the
                               ;; letters and the picture beside it lines
                               ;; up with what the eye sees rather than
                               ;; with the box the browser drew. Measured:
                               ;; four pixels of daylight in the display
                               ;; serif, three in the grotesque, and this
                               ;; is the one way to close both without a
                               ;; number that is right for one face only.
                               ;; Both ends, not only the top: the strip
                               ;; under the last line is the same half a
                               ;; line of air, and it is what the kicker
                               ;; underneath would otherwise be measured
                               ;; from.
                               ;;
                               ;; `text` and not `alphabetic` at the foot,
                               ;; though, which is the whole difference
                               ;; between a row that looked wrong and one
                               ;; that does not. Trimmed to the baseline,
                               ;; the box ended where the letters sit and
                               ;; the descenders hung out of it into the
                               ;; kicker's margin: a headline ending in
                               ;; `Polizei an` had four pixels of daylight
                               ;; under it and one ending in
                               ;; `ClojureScript` had none, in the same
                               ;; column, three rows apart. Trimmed to the
                               ;; font's own foot instead, the row carries
                               ;; the space a `g` would need whether or
                               ;; not there is one - the same distance in
                               ;; every row, which is the only way a
                               ;; distance is read as deliberate.
                               :textBoxTrim "trim-both"
                               :textBoxEdge "cap text"
                               :color (if unread? "text.primary" "text.secondary")})}
             title)
          ;; Where a paper sets its kicker: the feed's name and when the
          ;; article appeared, in small letter-spaced capitals, on the line
          ;; under the headline. The date and the clock time and not `2h`:
          ;; an age says how fresh a thing is, and what somebody reading a
          ;; feed of several papers wants to know is when it was written.
          ;; Four pixels under the headline's own foot, which with the
          ;; four of descender it now keeps and the two the capitals carry
          ;; over them comes out at ten from the last baseline: the kicker
          ;; is the headline's own second line and belongs to it, so this
          ;; is the smaller of the row's two distances - but only the
          ;; smaller. Set against the baseline it was six, and six under a
          ;; headline that size reads as a line that slipped rather than
          ;; one that was put there.
          ;;
          ;; `line-height: 1` because there is nothing here to lead - one
          ;; line of capitals with no descenders, in a box that would
          ;; otherwise be half again as tall as the letters in it and
          ;; would put that air where this margin is supposed to be.
          ($ mui/Box {:sx (sx {:display "flex" :alignItems "baseline" :gap ".5rem"
                               :marginTop ".25rem" :lineHeight 1
                               :fontFamily theme/font-ui :fontSize ".68rem"
                               :textTransform "uppercase" :letterSpacing ".1em"
                               :color "text.secondary"})}
             ;; The name gives way and the date never does: a date is read
             ;; as a whole or not at all, and a feed's name is recognised
             ;; from its first few letters. So this one shrinks (`0 1
             ;; auto`, and `min-width: 0` or a flex item refuses to go
             ;; narrower than its own text) and the one below does not -
             ;; the list's column is 24rem in a wide window and a long
             ;; paper's name used to push the clock time off the end of
             ;; the row.
             (when feed-title
               ($ :span {:style {:flex "0 1 auto" :minWidth 0 :maxWidth "12rem"
                                 :overflow "hidden"
                                 :textOverflow "ellipsis" :whiteSpace "nowrap"}}
                  feed-title))
             ;; And the date sits on the row's right edge, not behind the
             ;; name: `margin-left: auto` eats whatever is left over.
             ;; Two things on one line that both start where the text
             ;; before them ended are read as one phrase, and these are
             ;; two different facts - which paper, and when. Flush right
             ;; they also make a column of their own down the list, and a
             ;; date is the one thing here that is compared between rows,
             ;; which is what `tabular-nums` was already for.
             ($ :span {:style {:flex "none" :marginLeft "auto"
                               :fontVariantNumeric "tabular-nums"}}
                (or (u/at published) (u/at first-seen) "")))))))

(def-ui Masthead
  "Which paper this column is, over the first headline in it: a name, and
  under it the line that says what is in the list.

  A feed gives its own name and whatever it says about itself; a group
  gives the name somebody typed and the papers in it, because a group has
  nothing else to say about itself and the one thing worth knowing is which
  feeds are being read at once. `All feeds` has neither and gets no
  masthead - it is not a paper.

  It scrolls away with the list rather than sitting over it. A phone has
  room for five headlines, and a heading nailed to the top of the column
  costs one of them on every screenful - this one is read once, when the
  feed is opened, which is exactly when it is at the top."
  [title about]
  (let [about (not-empty (str/trim (str about)))]
    ;; The same left edge as the rows under it (`.75rem`, the one
    ;; `MuiListItemButton` is given in `leg.mui`): two headlines in one
    ;; column starting four pixels apart is a column that looks broken
    ;; rather than indented.
    ($ mui/Box {:sx (sx {:padding ".9rem .75rem .55rem"
                         :borderBottom "3px double" :borderColor "divider"})}
       ($ mui/Typography
          {:component "h2"
           :sx (sx {:fontFamily theme/font-head :fontWeight 900
                    :fontSize "1.3rem" :lineHeight 1.08
                    :letterSpacing "-.015em"})}
          title)
       ;; The standfirst: what the feed says it is, in the type a paper sets
       ;; its subheadings in. Two lines at most - a feed that writes a
       ;; paragraph about itself does not get to push the news off the
       ;; screen.
       (when about
         ($ mui/Typography
            {:sx (sx {:marginTop ".2rem" :fontFamily theme/font-body
                      :fontSize ".85rem" :lineHeight 1.35
                      :color "text.secondary"
                      :display "-webkit-box" :WebkitLineClamp 2
                      :WebkitBoxOrient "vertical" :overflow "hidden"})}
            about)))))

(def-ui Nothing [text]
  ($ mui/Box {:sx (sx {:padding "2rem 1rem" :textAlign "center" :color "text.secondary"})}
     ($ mui/Typography text)))

(def-ui ArticlesPane
  "The list, as a landmark. On a wide screen it is a section beside the
  article, which is the `main`; on a narrow one it is the whole screen and
  therefore the `main` itself - there is no second thing on it to be the
  one."
  [main?]
  (let [{:keys [articles feeds feed group filter article settings loading? busy?
                refreshing?]}
        (state/use-state)
        images? (u/images? (:reader/images settings))
        titles (into {} (map (juxt :feed/id #(or (:feed/title %) (:feed/url %))) feeds))
        open-id (:article/id article)
        ;; Only when one feed is being read: `All feeds` is not a paper and
        ;; has neither a name of its own nor anything to say about itself.
        ;; `some` and not `filter`, because `filter` is the name of the state
        ;; above and a keyword called as a function answers everything.
        this-feed (when feed (some #(when (= feed (:feed/id %)) %) feeds))
        ;; The papers in the group, as its standfirst. The window's own
        ;; feeds and not a second answer from the server: the list of
        ;; articles came from there, and these are the feeds carrying the
        ;; name, which is the same question asked of what is already here.
        in-group  (when group
                    (->> feeds
                         (filter #(= group (:feed/group %)))
                         (map u/feed-label)
                         (sort-by str/lower-case)))
        ;; Which list this is, for `leg.scroll`: its own address, filter
        ;; and all. Not the window's - the article that is open is in that
        ;; one and is not what is being scrolled - and not the feed id on
        ;; its own either: `Unread` and `All` are two lists of different
        ;; lengths, and the fortieth row of one is nowhere near the
        ;; fortieth row of the other.
        place (scroll/list-place (route/href {:feed feed :group group :filter filter}))
        ;; Not part of the place - what the rows are does not change which
        ;; list this is - but the restore below has to run again when they
        ;; arrive: on a phone the pane is drawn before them, and a row
        ;; cannot be scrolled to while it is not there yet.
        rows (hash (mapv :article/id articles))
        ;; Which row was held, and what to hang the menu off. Here and not
        ;; in `leg.state`, because it is not something the window is
        ;; looking at: it is a press, it is over as soon as it is answered,
        ;; and no address and no other pane has anything to say about it.
        [menu set-menu!] (kc/use-state nil)
        held (when menu (some #(when (= (:id menu) (:article/id %)) %) articles))
        span (when menu (state/unread-down-to articles (:id menu)))]
    ;; Back to the row somebody was at when they went into an article.
    ;; On the rows arriving as much as on the pane being drawn, because on
    ;; a phone it is drawn before they are here - and only while nobody has
    ;; moved it since (`to-row-untouched!`), so a list that grew under a
    ;; reading thumb is left where the thumb had it. A layout effect for
    ;; the same reason as in `leg.article/ArticlePane`: this pane is
    ;; swapped in inside a view transition's callback, and the position has
    ;; to be there before the browser's first picture of it.
    (kc/use-layout-effect
     [place rows]
     (scroll/to-row-untouched! (.querySelector js/document "[data-scroll=\"list\"]")
                               place))
    ($ mui/Box {:component (if main? "main" "section")
                :aria-label "Articles"
                :className "leser-vt-list"
                :sx (sx {:height "100%" :minHeight 0
                         :display "flex" :flexDirection "column"})}
       (when (or busy? loading?) ($ mui/LinearProgress))
       ($ mui/Box {:sx (sx {:flex "1" :minHeight 0 :overflowY "auto"})
                   ;; The box that scrolls, named so it can be found again
                   ;; from the effect above - the same arrangement as
                   ;; `data-keys` on the article's box, and for the same
                   ;; reason: what wants it is not what drew it.
                   :data-scroll "list"
                   :onScroll (scroll/on-list-scroll place)}
          (cond
            this-feed ($ Masthead {:title (u/feed-label this-feed)
                                   :about (:feed/description this-feed)})
            group     ($ Masthead {:title group
                                   :about (str/join " · " in-group)})
            :else     nil)
          (cond
            (and (empty? articles) loading?) nil
            (and (empty? articles) (empty? feeds))
            ($ Nothing {:text "No feeds yet. Add one, or import a collection."})

            (empty? articles)
            ($ Nothing {:text (if (= :unread filter) "Nothing unread." "Nothing here.")})

            :else
            ($ mui/List {:disablePadding true
                         :data-keys "list"
                         :data-list "articles"}
               (for [a articles]
                 ($ Row {:key (:article/id a)
                         :article a
                         :selected? (= open-id (:article/id a))
                         :feed-title (when-not feed (get titles (:article/feed a)))
                         :image? images?
                         :on-open state/open-article!
                         :on-menu (fn [id el]
                                    (set-menu! {:id id :anchor el
                                                :at (js/Date.now)}))})))))
       ;; What can be done at the line somebody held: this one article, and
       ;; everything down to it. Two questions and not one, because a row
       ;; is both a thing and a place - the article somebody is done with
       ;; or wants back, and the line a list read from the top has got to.
       ;;
       ;; The count is on the label, which the dialog under [Mark read]
       ;; deliberately does not have (`leg.dialogs/MarkRead`): there the
       ;; window cannot know what the server would mark, here it is exactly
       ;; the rows above the finger.
       ($ mui/Menu
          {:open (boolean menu)
           :anchorEl (:anchor menu)
           ;; Nothing typed into the menu reaches the window's own keys
           ;; (`leg.keys`), which would otherwise walk the list about
           ;; behind it. The menu's own keys - the arrows, Escape - are
           ;; handled below this element and are unaffected.
           :onKeyDown (fn [^js e] (.stopPropagation e))
           :onClose (fn [_]
                      (when (< menu-grace (- (js/Date.now) (:at menu 0)))
                        (set-menu! nil)))}
          ;; Which row this is about, and the way out. A menu hanging off
          ;; a row in a list of twenty near-identical rows is a menu whose
          ;; subject is a guess; the headline across the top says what
          ;; `up to here` means before anything is pressed. Not a
          ;; `MenuItem`: nothing happens when it is pressed, and a line in
          ;; a menu that does nothing is a line somebody presses twice.
          ;;
          ;; The cross is for the thumb that opened this by accident.
          ;; Escape and a press outside both close it already, but neither
          ;; is on the screen, and a menu a phone cannot see its way out of
          ;; is a menu answered by pressing something.
          ($ mui/Box
             {:sx (sx {:display "flex" :alignItems "center" :gap ".25rem"
                       :padding ".15rem .15rem .15rem .75rem"
                       :borderBottom "1px solid"
                       :borderColor "divider"})}
             ($ mui/Box
                {:sx (sx {:flex 1 :minWidth 0 :maxWidth "14rem"
                          :overflow "hidden" :textOverflow "ellipsis"
                          :whiteSpace "nowrap"
                          :fontSize ".72rem" :letterSpacing ".08em"
                          :textTransform "uppercase"
                          :color "text.secondary"})}
                (:article/title held))
             ($ mui/Box
                {:component "button"
                 :type "button"
                 :aria-label "Close"
                 :onClick (fn [_] (set-menu! nil))
                 :sx (sx {:flex "none" :display "flex" :alignItems "center"
                          :justifyContent "center" :lineHeight 0
                          :appearance "none" :background "none" :border 0
                          :padding ".2rem" :cursor "pointer"
                          :borderRadius "4px"
                          :color "text.secondary"
                          "&:hover" {:color "text.primary"}
                          :minWidth "2.25rem" :minHeight "2.25rem"
                          "@media (pointer: coarse)" {:minWidth "2.75rem"
                                                      :minHeight "2.75rem"}})}
                ;; Drawn and not typed: a glyph is a thing a system font
                ;; is allowed not to have, and a cross that renders as an
                ;; empty box is not a way out.
                ($ :svg {:width ".85rem" :height ".85rem" :viewBox "0 0 24 24"
                         :aria-hidden true :focusable "false"}
                   ($ :path {:d "M5 5 L19 19 M19 5 L5 19"
                             :fill "none"
                             :stroke "currentColor"
                             :strokeWidth 2
                             :strokeLinecap "round"}))))
          ;; The row itself first: it is what was pressed, and it is one
          ;; article rather than a span of them. One item and not two,
          ;; because a row is read or it is not, and
          ;; `leg.state/set-read!` already takes the answer.
          ($ mui/MenuItem
             {:onClick (fn [_]
                         (set-menu! nil)
                         (state/set-read! (:id menu) (not (:article/read held))))}
             (if (:article/read held) "Mark unread" "Mark read"))
          ($ mui/MenuItem
             {:disabled (empty? span)
              :onClick (fn [_]
                         (set-menu! nil)
                         (state/read-down-to! (:id menu)))}
             (if (empty? span)
               "Nothing unread up to here"
               (str "Mark " (count span) " read up to here"))))
       ;; Under the list, in the same place as everywhere else: where the
       ;; window is going on the left, what can be done to this list on the
       ;; right, and the filter held in the middle - it is neither of those,
       ;; it is what the list is being read with, and it is pressed often
       ;; enough to deserve the spot a thumb rests on.
       ($ nav/Bar
          {:label "Articles"
           ;; [Home] only when this list is the whole screen. On a wide one
           ;; the sidebar is to the left of it and carries the [Home] of the
           ;; window; three of them in a row would be three ways to the same
           ;; place, all of them already on screen.
           :left (when main? ($ nav/Home))
           :middle ($ nav/Filters {:current filter
                                   :options [[:unread "Unread"] [:all "All"]
                                             [:starred "Starred"]]
                                   :on-change state/set-filter!})
           :right ($ :<>
                     ;; The question and not the press: which of them to
                     ;; mark is asked in a dialog, because "mark read" on
                     ;; its own never said (see leg.dialogs/MarkRead).
                     ($ mui/Button {:disabled busy?
                                    :onClick (fn [_] (state/dialog! :mark-read))}
                        "Mark read")
                     ;; The press that is waited on, and the only button
                     ;; here that says so: while the fetch runs the word
                     ;; keeps its ink and breathes (`leg.nav/Bar`). The
                     ;; state is `aria-busy` rather than a `data-` word of
                     ;; our own, because it is already the thing being
                     ;; said - this control is working on what was asked
                     ;; of it - and a screen reader knows it.
                     ($ mui/Button {:disabled busy?
                                    :aria-busy (boolean refreshing?)
                                    :onClick (fn [_] (state/refresh!))}
                        "Refresh"))}))))
